import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronLeft, type LucideIcon } from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useIsMobile } from "@/hooks/use-mobile";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { saveListingsScrollY } from "@/lib/native/listingsScrollRestore";
import { useTheme } from "@/context/ThemeContext";
import { useEffect, useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import { cn } from "@/lib/utils";

interface BtnNavigateBackProps {
  to?: string;
  label?: string;
  /** Optional state to pass when navigating (e.g. to restore list page/filters) */
  state?: object;
  /** Prefer browser history when no location.state.from is set (listing detail). */
  preferHistory?: boolean;
  /** Round overlay button (e.g. CarDetails gallery). */
  overlay?: boolean;
  /** Icon only — keeps aria-label from `label`. */
  iconOnly?: boolean;
  /** Override the default back/chevron icon (e.g. Home for För dig mobile). */
  icon?: LucideIcon;
  className?: string;
}

const BtnNavigateBack = ({
  to = "/dashboard",
  label = "Dashboard",
  state,
  preferHistory = false,
  overlay = false,
  iconOnly = false,
  icon: IconOverride,
  className,
}: BtnNavigateBackProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const compactNative = isNative && isMobile;
  const { theme } = useTheme();
  const isDark = theme === "dark";
  const [hasProgress, setHasProgress] = useState(false);
  const { language } = useLanguage();
  const vLabels = valuationLabels(language);

  useEffect(() => {
    const handleProgressUpdate = (event: CustomEvent) => {
      setHasProgress(event.detail.hasProgress);
    };

    window.addEventListener(
      "carPostAdProgress",
      handleProgressUpdate as EventListener,
    );
    window.addEventListener(
      "carValuationProgress",
      handleProgressUpdate as EventListener,
    );

    return () => {
      window.removeEventListener(
        "carPostAdProgress",
        handleProgressUpdate as EventListener,
      );
      window.removeEventListener(
        "carValuationProgress",
        handleProgressUpdate as EventListener,
      );
    };
  }, []);

  const handleNavigation = () => {
    if (hasProgress) {
      const confirmed = window.confirm(vLabels.leaveConfirm);
      if (!confirmed) {
        return;
      }
    }

    const navState = location.state as {
      from?: string;
      fromScrollY?: number;
    } | null;
    if (navState?.from) {
      if (navState.fromScrollY != null) {
        try {
          const fromUrl = new URL(navState.from, window.location.origin);
          saveListingsScrollY(
            fromUrl.pathname,
            fromUrl.search,
            navState.fromScrollY,
          );
        } catch {
          saveListingsScrollY(
            navState.from.split("?")[0] ?? navState.from,
            navState.from.includes("?")
              ? `?${navState.from.split("?").slice(1).join("?")}`
              : "",
            navState.fromScrollY,
          );
        }
      }
      navigate(navState.from, {
        state:
          navState.fromScrollY != null
            ? { restoreScrollY: navState.fromScrollY }
            : undefined,
      });
      return;
    }

    if (preferHistory && window.history.length > 1) {
      navigate(-1);
      return;
    }

    navigate(to, state != null ? { state } : undefined);
  };

  const Icon =
    IconOverride ?? (compactNative || overlay ? ChevronLeft : ArrowLeft);

  if (overlay) {
    return (
      <button
        type="button"
        onClick={handleNavigation}
        aria-label={label}
        className={cn(
          "fixed z-50 inline-flex items-center justify-center rounded-full p-3.5",
          "bg-neutral-400/80 text-white backdrop-blur-md",
          "shadow-[0_2px_12px_rgba(0,0,0,0.28)] ring-1 ring-white/30",
          "active:scale-95 transition-transform duration-150 touch-manipulation",
          "top-[calc(var(--native-safe-top,env(safe-area-inset-top,0px))+12px)] left-[12px]",
          className,
        )}
      >
        <Icon className="h-6 w-6 shrink-0" strokeWidth={2.5} />
      </button>
    );
  }

  return (
    <Button
      type="button"
      variant="ghost"
      onClick={handleNavigation}
      aria-label={label}
      className={cn(
        "inline-flex w-fit items-center justify-start gap-2",
        compactNative
          ? "h-9 px-1 mb-1 -ml-0.5 text-sm font-medium self-start"
          : "mb-3",
        isDark
          ? "text-white/80 hover:text-white hover:backdrop-blur"
          : "text-black/80 hover:text-black hover:backdrop-blur",
        !compactNative && isMobile && "text-sm",
        iconOnly && "justify-center gap-0 px-2",
        className,
      )}
    >
      <Icon className={compactNative || iconOnly ? "h-5 w-5 shrink-0" : isMobile ? "h-3 w-3" : "h-4 w-4"} />
      {!iconOnly && label}
    </Button>
  );
};

export default BtnNavigateBack;
